import { useTable } from "react-table";
import { useEffect, useMemo, useState } from "react";
import {
  defaultDateDisplay,
  formatCurrency,
  getUrlQuerysection,
} from "../../../helpers";
import { useNavigate, useParams } from "react-router-dom";
import primaryComponents from "../../primaryComponents";
import secondaryComponents from "../../secondaryComponents";
import { DeleteIcon } from "../../../assets/svgIcons";
import { pathConstants } from "../../../routes/pathContants";
import { useGetInfo } from "../../../hooks/useGetInfo";
import { useUrlSearch } from "../../../hooks/useUrlSearch";
import { useGetOrderList } from "../../../hooks/api/queries/useOrder";

export const useBillsWidget = () => {
  const queries = getUrlQuerysection();
  const navigate = useNavigate();
  const { store_id } = useParams();
  const { getStoreInfo } = useGetInfo();
  const storeDetails = getStoreInfo(store_id);
  const showKitchen = storeDetails?.allow_section;
  const { search, setSearch, gotoPage, filterByKey, filterBySearch } =
    useUrlSearch();
  const [isOrderCancel, setIsOrderCancel] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [billWasUpdated, setBillWasUpdated] = useState(null);
  const { isLoading: isFetchingBills, data: bills } = useGetOrderList({
    queries,
    isEnabled: true,
  });

  useEffect(() => {
    if (!search.get("store_id") || search.get("payment_method")) {
      search.set("store_id", store_id);
      search.set("payment_method", "bill");
      setSearch(search, { replace: true });
    }
  }, []);

  const columns = useMemo(() => {
    const headers1 = [
      {
        Header: "Order Date/Time",
        accessor: "created_at",
        Cell: ({ cell: { value } }) => (
          <span>
            {value === null || value === undefined
              ? "N/A"
              : defaultDateDisplay(value)}
          </span>
        ),
      },
      {
        Header: "Order Number",
        accessor: "id",
        Cell: ({ cell: { value } }) => (
          <span
            className="purple--text cursor-pointer"
            onClick={() => {
              setSelectedOrder(value);
            }}
            style={{
              textDecoration: "underline",
            }}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "Customer Name",
        accessor: "customer",
        Cell: ({ cell: { value } }) =>
          value === null
            ? "N/A"
            : value?.first_name +
              (value?.last_name ? " " + value?.last_name : ""),
      },
      {
        Header: "Table",
        accessor: "table",
        Cell: ({ cell: { value } }) => (value === null ? "N/A" : value?.name),
      },
    ];
    const headers2 = [
      {
        Header: "Cashier",
        accessor: "cashier",
        Cell: ({ cell: { value } }) => (
          <span>
            {value === null || value === undefined ? "N/A" : value?.first_name}
          </span>
        ),
      },
      {
        Header: "Amount",
        accessor: "total_amount",
        Cell: ({ cell: { value } }) => (
          <span>
            {formatCurrency({
              value: value,
              currencyCode: storeDetails?.currency_code,
            })}
          </span>
        ),
      },
      {
        Header: "Actions",
        accessor: "",
        Cell: ({ row }) => {
          return (
            <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <primaryComponents.Button
                classNames="btn btn--primary smallBtn"
                isDisabled={row.original?.is_paid}
                onClick={() => {
                  navigate(
                    pathConstants.SELL_MODE_BILL_CHECKOUT({
                      store_id,
                      order_id: row.original?.id,
                    }),
                  );
                }}
              >
                Checkout
              </primaryComponents.Button>
              <primaryComponents.Button
                classNames="btn btn--outline smallBtn"
                isDisabled={row.original?.is_paid}
                onClick={() => {
                  setBillWasUpdated(row?.original?.id);
                }}
              >
                Update
              </primaryComponents.Button>
              <secondaryComponents.NavigateWithPermission
                permissionParentKey={"Inventory Management"}
                permissionChildKey="can_cancel_bill"
              >
                <span
                  onClick={() => {
                    setIsOrderCancel(row?.original);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <DeleteIcon />
                </span>
              </secondaryComponents.NavigateWithPermission>
            </span>
          );
        },
      },
    ];

    let headers = [];
    if (showKitchen)
      headers = [
        ...headers1,
        {
          Header: "Docket Status",
          accessor: "is_section_items_printed",
          Cell: ({ cell: { value } }) =>
            !value ? (
              <span
                style={{ padding: "3px 5px", textWrap: "nowrap" }}
                className="smallest-text-size pending--squared"
              >
                Newly Updated
              </span>
            ) : (
              "N/A"
            ),
        },
        ...headers2,
      ];
    else headers = [...headers1, ...headers2];

    return headers;
  }, []);

  const instance = useTable({ columns, data: bills?.data || [] });

  return {
    instance,
    bills: bills?.data || [],
    isFetchingBills,
    billPageDetails: bills?.meta || {},
    selectedOrder,
    isOrderCancel,
    billWasUpdated,
    search,
    setSearch,
    setSelectedOrder,
    gotoPage,
    filterBySearch,
    filterByKey,
    setIsOrderCancel,
    setBillWasUpdated,
  };
};
